/**
 * How a lazily-loaded filter picker assembles the list it shows.
 *
 * A picker that pages a vocabulary too large to hold — thousands of site groups
 * on one customer — has two sources for what it displays: the pages it has
 * fetched for the current search text, and whatever the user already chose,
 * which may live on a page it will never load. The chosen one has to stay
 * visible and selected, or the only way to undo a filter is to guess which page
 * it came from.
 *
 * Contains:
 * - appendPage — fold a fetched page into the options already held, without duplicates
 * - withSelectedOption — pin the current selection to the top of what is shown
 *
 * Returns plain JS — no JSX, no redux.
 */

/**
 * Append a freshly fetched page, dropping anything already held. The server can
 * repeat a row across pages when rows are written between requests; a repeat is
 * a duplicate to the reader, not a second option.
 *
 * @param {{ value: string, label: string }[]} held - options loaded so far.
 * @param {{ value: string, label: string }[]} page - the page just fetched.
 * @returns {{ value: string, label: string }[]} a new array; `held` is untouched.
 */
export function appendPage(held, page) {
  const seen = new Set((held ?? []).map(option => option.value));
  const added = (page ?? []).filter(option => option?.value != null && !seen.has(option.value));
  return [...(held ?? []), ...added];
}

/**
 * Put the current selection at the head of the list, whether or not a loaded
 * page happens to contain it.
 *
 * @param {{ value: string, label: string }[]} options - the loaded options.
 * @param {?{ value: string, label: string }} selected - the resolved selection,
 *   or null when nothing is chosen or its label has not resolved yet.
 * @returns {{ value: string, label: string }[]} a new array; inputs are untouched.
 */
export function withSelectedOption(options, selected) {
  if (selected?.value == null) return options ?? [];
  return [selected, ...(options ?? []).filter(option => option.value !== selected.value)];
}
